import {closeDialog, openDialog} from 'platform/components';

import {isString} from 'ramda-adjunct';

import {
  usePostServiceItemsMaterialPartRequestCancellationMutation,
  usePostServiceItemsMaterialPartRequestMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError, refreshDmsDatagrid} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {getRowCellValue, type GridDataChange, type RowData} from 'features/eag-grid';

import {IssueMaterialItems} from '../components/IssueMaterialItems';
import {JOB_ITEMS_GRID_CODE} from '../constants/jobItemsGridCode';
import {SERVICE_ITEMS_GRID_COLUMNS} from '../constants/serviceItemsGridColumns';
import {getDefaultMechanicId} from '../utils/getDefaultMechanicId';
import {getWorkItemRowsWithAction} from '../utils/getWorkItemRowsWithAction';

const ISSUE_ACTION_KEY = 'issue';
const REQUEST_ACTION_KEY = 'request';
const REQUEST_CANCELLATION_ACTION_KEY = 'requestCancellation';
const ISSUE_DIALOG_ID = 'issueMaterialItem';

type UseWorkItemsBulkActionsParams = TestIdProps & {
  gridData: GridDataChange | null;
  serviceCaseId: string;
  serviceOrderId: string | Nullish;
  serviceJobId: string | Nullish;
};

const toServiceItems = (rows: RowData[], serviceJobId: string) =>
  rows
    .map((row) => getRowCellValue(row, SERVICE_ITEMS_GRID_COLUMNS.serviceOrderJobItemId))
    .filter(isString)
    .map((serviceItemId) => ({serviceItemId, serviceJobId}));

/**
 * "More actions" bulk operations over a job's work items (T20-128140): issue material,
 * request material, and request cancellation, each applied to EVERY loaded row whose
 * backend actions expose the corresponding key (the same applicability signal the grid
 * uses per row). Counts feed the dropdown labels; rows come from onGridDataChange.
 *
 * Gating is strictly per action, never menu-wide: each mutation's in-flight state
 * disables (and spins) only its own item, so running one action leaves the others
 * usable. Per-item disabling is also what protects against double-submit, since an
 * already-open Dropdown stays interactive even when its trigger is disabled.
 */
export const useWorkItemsBulkActions = (params: UseWorkItemsBulkActionsParams) => {
  const [postServiceItemsMaterialPartRequest, requestMaterialState] =
    usePostServiceItemsMaterialPartRequestMutation();
  const [postServiceItemsMaterialPartRequestCancellation, requestCancellationState] =
    usePostServiceItemsMaterialPartRequestCancellationMutation();

  const rows = params.gridData?.rows ?? [];
  const issueRows = getWorkItemRowsWithAction(rows, ISSUE_ACTION_KEY);
  const requestRows = getWorkItemRowsWithAction(rows, REQUEST_ACTION_KEY);
  const cancellationRows = getWorkItemRowsWithAction(rows, REQUEST_CANCELLATION_ACTION_KEY);

  const serviceJobId = params.serviceJobId ?? '';
  const serviceOrderId = params.serviceOrderId ?? '';
  const refreshItems = () => refreshDmsDatagrid(JOB_ITEMS_GRID_CODE);

  const issueMaterial = () =>
    openDialog(
      <IssueMaterialItems
        serviceCaseId={params.serviceCaseId}
        serviceOrderId={serviceOrderId}
        items={toServiceItems(issueRows, serviceJobId)}
        defaultMechanicId={getDefaultMechanicId(issueRows)}
        onSubmit={refreshItems}
        onClose={() => closeDialog(ISSUE_DIALOG_ID)}
        data-testid={suffixTestId(ISSUE_DIALOG_ID, params)}
      />,
      {
        title: i18n.t('entity.orderRequest.labels.issueMaterialItem'),
        id: ISSUE_DIALOG_ID,
        size: 'small',
      }
    );

  const requestMaterial = () =>
    postServiceItemsMaterialPartRequest({
      body: {serviceItems: toServiceItems(requestRows, serviceJobId)},
      serviceCaseId: params.serviceCaseId,
      serviceOrderId,
    })
      .unwrap()
      .then(refreshItems)
      .catch(handleApiError);

  const requestCancellation = () =>
    postServiceItemsMaterialPartRequestCancellation({
      body: {serviceItems: toServiceItems(cancellationRows, serviceJobId)},
      serviceCaseId: params.serviceCaseId,
      serviceOrderId,
    })
      .unwrap()
      .then(refreshItems)
      .catch(handleApiError);

  return {
    /** In-flight state of each mutation — gates (and spins) only its own item. */
    isRequestMaterialPending: requestMaterialState.isLoading,
    isRequestCancellationPending: requestCancellationState.isLoading,
    issueCount: issueRows.length,
    requestCount: requestRows.length,
    cancellationCount: cancellationRows.length,
    issueMaterial,
    requestMaterial,
    requestCancellation,
  };
};
